import type { WeaponDef } from "./types";
import { hex } from "./color";

export const CELL = 2;
export const EMPTY = 0;
export const BACKGROUND = 1;
export const SOLID = 2;
export const DEBRIS = 3;

export const DT = 1 / 60;
export const MAX_STEPS = 4;
export const MAX_GLYPHS = 26000;
export const MAX_H = 2600;
export const LAYOUT_MIN = 1024;
export const LAYOUT_MAX = 1680;
export const TOUCH_MIN = 320;
export const BAKE_VIEW_H = 820;
export const LETTER_CAP = 1400;
export const PARTICLE_CAP = 24000;
export const FIRE_CAP = 7000;
export const GRAVITY = 1900;

export const RUN = 255;
export const GROUND_ACCEL = 3600;
export const GROUND_DECEL = 4600;
export const AIR_ACCEL = 1650;
export const AIR_DECEL = 700;
export const JUMP_VY = 650;
export const FLIP_VY = 590;
export const COYOTE = 0.09;
export const JUMP_BUFFER = 0.12;
export const SNAP_UP = 7;
export const SNAP_DOWN = 9;
export const JET_SPEED = 330;
export const JET_ACCEL = 2300;
export const JET_DECEL = 900;
export const JET_THRUST = 3700;
export const JET_MAX_RISE = 430;
export const JET_IGNITE = 0.14;
export const BODY_HALF = 6;
export const BODY_H = 58;

export const ROCKET_R = 46;
export const GRENADE_R = 50;
/** Seconds before another grenade can leave the hand. */
export const GRENADE_CD = 0.55;
export const BOMBLET_R = 30;

export const ADJECTIVES = ["Sneaky", "Turbo", "Wobbly", "Angry", "Rusty", "Shiny", "Fuzzy", "Mighty", "Sleepy", "Spicy", "Crispy", "Grumpy", "Jumpy", "Lucky", "Rapid", "Silent"];
export const NOUNS = ["Popup", "Navbar", "Captcha", "Cookie", "Footer", "Banner", "Pixel", "Favicon", "Iframe", "Div", "Tooltip", "Modal", "Sidebar", "Spinner", "Button", "Widget"];

export const ROOM_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
export const PROTOCOL = 4;
/** Live client constant is 5. This build allows 6, matching the page's "up to about six players" copy. */
export const MAX_PLAYERS = 6;
export const KILL_TARGET = 10;
export const MATCH_MS = 90000;

export const MP_BANNED = ["tesla", "meteor", "overkill", "railgun"];

/** Coin price for every unlockable hero. Stickman stays free. */
export const COIN_HERO = 400;
export const COIN_IRON = COIN_HERO;
export const COIN_DIGGER = COIN_HERO;
export const COIN_CLOWN = COIN_HERO;
export const COIN_HEX = COIN_HERO;
export const COIN_NOVA = COIN_HERO;

/** Stickman free hotbar length (keys 1–8). Exclusives follow and share key 0. */
export const FREE_WEAPON_COUNT = 8;

export const TINTS: Array<[number, number]> = [
  [hex("#ff7a2e"), hex("#c24c17")],
  [hex("#3ec7ff"), hex("#1b78ad")],
  [hex("#8dff4d"), hex("#4a9e27")],
  [hex("#ff5fae"), hex("#b0336c")],
  [hex("#ffd84d"), hex("#b08a1a")],
  [hex("#b98cff"), hex("#6c4aad")],
];

export const NEAR = hex("#ff7a2e");
export const FAR = hex("#c24c17");
export const OUTLINE = hex("#1a1422");
export const WHITE = hex("#ffffff");

export const BACKGROUNDS = [
  {
    id: "wreck",
    name: "Wreck night",
    procedural: true,
    layers: [] as Array<[string, number]>,
  },
  {
    id: "mountain",
    name: "Ridge dusk",
    layers: [
      ["ridge-sky", 0.01],
      ["ridge-far", 0.03],
      ["ridge-mid", 0.06],
      ["ridge-trees", 0.1],
      ["ridge-front", 0.16],
    ] as Array<[string, number]>,
  },
  { id: "sunny", name: "Ring meadow", layers: [["meadow", 0.03]] as Array<[string, number]> },
  {
    id: "cities",
    name: "Harbor night",
    layers: [
      ["harbor-sky", 0.03],
      ["harbor-skyline", 0.08],
      ["harbor-docks", 0.16],
    ] as Array<[string, number]>,
  },
];

export const SPRITES = [
  "pistol", "smg", "shotgun", "launcher", "railgun", "flamethrower", "overkill", "remote",
  "tesla", "meteor", "grenade", "rocket", "mirv", "bomblet", "parachute", "jetpack", "drone", "gatling",
];

function w(
  id: string,
  name: string,
  kind: WeaponDef["kind"],
  auto: boolean,
  cd: number,
  pellets: number,
  spread: number,
  speed: number,
  pierce: number,
  dmg: number,
  push: number,
  kick: number,
  kickRot: number,
  shake: number,
  flash: number,
  holdDist: number,
  holdDrop: number,
  twoHanded = true,
  grasp = false,
  sprite = id,
): WeaponDef {
  return { id, name, kind, auto, cd, pellets, spread, speed, pierce, dmg, push, kick, kickRot, shake, flash, twoHanded, holdDist, holdDrop, grasp, sprite };
}

export const WEAPONS: WeaponDef[] = [
  w("pistol", "Pistol", "bullet", false, 0.16, 1, 0.012, 2875, 6, 16, 0, 4, 0.42, 0.12, 1.15, 21, 7, false, true),
  w("smg", "SMG", "bullet", true, 0.068, 1, 0.07, 2500, 4, 8, 14, 2.6, 0.12, 0.07, 0.95, 12, 12),
  w("shotgun", "Shotgun", "bullet", false, 0.62, 10, 0.17, 2190, 3, 12, 230, 7, 0.55, 0.42, 1.9, 8, 7),
  w("launcher", "Rocket", "rocket", false, 0.85, 1, 0, 420, 0, 0, 120, 6, 0.3, 0.3, 1.5, 2, 12, true, false, "launcher"),
  w("railgun", "Railgun", "rail", false, 1.1, 1, 0, 0, 999, 130, 260, 9, 0.34, 0.55, 1.6, 8, 10),
  w("flamer", "Flamethrower", "flame", true, 1 / 60, 5, 0.1, 560, 0, 0, 0, 0.5, 0.015, 0.012, 0, 8, 9, true, false, "flamethrower"),
  w("overkill", "Too Much", "mirv", false, 1.9, 1, 0, 760, 0, 0, 300, 11, 0.55, 0.55, 2.2, 1, 11),
  w("drone", "Drone", "drone", true, 0.4, 1, 0.05, 2915, 5, 16, 0, 1.5, 0.1, 0.02, 1.3, 4, 13, true, false, "remote"),
  w("magnet", "Magnet", "magnet", false, 0.25, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 18, 6, false, false, "magnet"),
  w("drill", "Drill Charge", "drill", false, 1.3, 1, 0, 760, 0, 220, 78, 5.2, 0.3, 0.24, 1.25, 8, 9, true, false, "drill"),
  w("punchline", "Punchline", "punch", false, 1.0, 1, 0, 2200, 0, 165, 180, 1, 0.2, 0.62, 0, 16, 7, false, true, "punchline"),
  w("meteor", "Meteor", "meteor", false, 1.0, 1, 0, 0, 0, 0, 0, 3, 0.4, 0.1, 0, 16, 8, false, true),
  w("tesla", "Tesla Coil", "tesla", false, 1.6, 1, 0, 620, 0, 0, 28, 5, 0.22, 0.16, 0, 12, 8),
];

export const PLAYER_BULLET_DMG: Record<string, number> = { pistol: 22, smg: 9, shotgun: 11, drone: 5 };
export const PLAYER_BLAST_DMG: Record<string, number> = { rocket: 95, grenade: 85, overkill: 40, drone: 60, drill: 140 };

/** Ids a multiplayer snapshot may draw. Anything else, including retired Halloween skins, is Stickman. */
const PLAYABLE_SKINS = new Set(["stickman", "iron", "digger", "clown", "hex", "nova"]);

export function canonicalSkin(id: string): string {
  return PLAYABLE_SKINS.has(id) ? id : "stickman";
}

export const CHECKOUT = "/";
export const RESTORE = "/";
/** Default page for an empty address, the Demo button, and a share with no target. */
export const DEMO_URL = "https://archive.org/";

export function randomName(): string {
  const a = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
  const b = NOUNS[Math.floor(Math.random() * NOUNS.length)];
  return `${a} ${b}`;
}

export function layoutFor(stageW: number, narrowTouch: boolean, dpr: number): { layoutW: number; maxH: number } {
  if (!narrowTouch || stageW >= LAYOUT_MIN) {
    return { layoutW: Math.max(LAYOUT_MIN, Math.min(LAYOUT_MAX, stageW)), maxH: MAX_H };
  }
  const layoutW = Math.max(TOUCH_MIN, stageW);
  const zMem = 8e6 / (layoutW * dpr * dpr);
  const v = MAX_H * Math.min(2, LAYOUT_MIN / layoutW);
  return { layoutW, maxH: Math.round(Math.max(MAX_H, Math.min(v, zMem))) };
}

/**
 * Render zoom. Matches the original split: F8 when the page is no wider than
 * the layout width, JQ (pixel budget) only when a baked map or widthFor grew
 * the page past that width. A tall Demo that fits the screen stays at the
 * fill zoom, so a landscape phone does not letterbox it to ~75%.
 */
export function zoomFor(worldW: number, worldH: number, cssW: number, dpr: number, touch: boolean): number {
  const fill = Math.max(1, cssW / worldW);
  const uncapped = Math.max(1, Math.round(CELL * dpr * fill)) / CELL;
  if (worldW <= cssW + 0.5) return uncapped;
  const budget = touch ? 8e6 : LAYOUT_MAX * 3 * MAX_H * 3;
  return Math.max(1, Math.min(Math.round(CELL * dpr * fill), Math.floor(CELL * Math.sqrt(budget / (worldW * worldH))))) / CELL;
}

export const MAP_FONT_CSS = `
@font-face { font-family: "Impact"; src: url("/assets/maps/fonts/Anton-Regular.ttf") format("truetype"); font-weight: 100 900; font-style: normal; font-display: swap; }
@font-face { font-family: "Arial Black"; src: url("/assets/maps/fonts/ArchivoBlack-Regular.ttf") format("truetype"); font-weight: 100 900; font-style: normal; font-display: swap; }
@font-face { font-family: "Arial"; src: url("/assets/maps/fonts/Arimo-Regular.ttf") format("truetype"); font-weight: 400; font-style: normal; font-display: swap; }
@font-face { font-family: "Arial"; src: url("/assets/maps/fonts/Arimo-Bold.ttf") format("truetype"); font-weight: 700; font-style: normal; font-display: swap; }
@font-face { font-family: "Arial"; src: url("/assets/maps/fonts/Arimo-Italic.ttf") format("truetype"); font-weight: 400 700; font-style: italic; font-display: swap; }
@font-face { font-family: "Courier New"; src: url("/assets/maps/fonts/Cousine-Regular.ttf") format("truetype"); font-weight: 400; font-style: normal; font-display: swap; }
@font-face { font-family: "Courier New"; src: url("/assets/maps/fonts/Cousine-Bold.ttf") format("truetype"); font-weight: 700; font-style: normal; font-display: swap; }
@font-face { font-family: "Georgia"; src: url("/assets/maps/fonts/Gelasio-wght.ttf") format("truetype"); font-weight: 400 700; font-style: normal; font-display: swap; }
`;
